// Emissions intensity factors (kg CO2e per MWh)
// Based on Australian National Greenhouse Accounts factors
export const EMISSIONS_FACTORS: Record<string, number> = {
  coal_black: 950,
  coal_brown: 1250,
  gas_ccgt: 450,
  gas_ocgt: 650,
  gas_recip: 600,
  gas_steam: 550,
  distillate: 700,
  solar_utility: 0,
  wind: 0,
  hydro: 0,
  battery_charging: 0, // Charging itself doesn't emit
  battery_discharging: 0, // Storage, no direct emissions
  pumps: 0, // Pumped hydro storage
  bioenergy_biomass: 50, // Near-zero (carbon neutral)
};

/**
 * Calculate emissions for a given fuel type and power output
 * @param fueltech - Fuel technology type
 * @param outputMW - Power output in MW
 * @param durationHours - Duration in hours (default 1)
 * @returns Emissions in tonnes CO2e
 */
export const calculateEmissions = (
  fueltech: string,
  outputMW: number,
  durationHours: number = 1
): number => {
  const factor = EMISSIONS_FACTORS[fueltech] || 0;
  // Convert kg to tonnes (÷ 1000)
  return (factor * outputMW * durationHours) / 1000;
};

/**
 * Calculate total emissions for all generators at a given timestamp
 * @param generators - Array of generators with output data
 * @param durationHours - Duration in hours
 * @returns Total emissions in tonnes CO2e
 */
export const calculateTotalEmissions = (
  generators: Array<{ fueltech: string; output: number }>,
  durationHours: number = 1
): number => {
  return generators.reduce((total, gen) => {
    return total + calculateEmissions(gen.fueltech, gen.output, durationHours);
  }, 0);
};

/**
 * Calculate average emissions intensity for a set of generators
 * @param generators - Array of generators with output data
 * @returns Average emissions intensity in kg CO2e/MWh
 */
export const calculateAverageIntensity = (
  generators: Array<{ fueltech: string; output: number }>
): number => {
  const totalOutput = generators.reduce((sum, gen) => sum + gen.output, 0);
  if (totalOutput === 0) return 0;

  const totalEmissions = generators.reduce((sum, gen) => {
    const factor = EMISSIONS_FACTORS[gen.fueltech] || 0;
    return sum + factor * gen.output;
  }, 0);

  return totalEmissions / totalOutput;
};

/**
 * Get color for emissions intensity (for visualization)
 * @param intensity - Emissions intensity in kg CO2e/MWh
 * @returns Hex color code
 */
export const getEmissionsColor = (intensity: number): string => {
  if (intensity === 0) return "#4CAF50"; // Green - zero emissions
  if (intensity < 200) return "#8BC34A"; // Light green - very low
  if (intensity < 400) return "#FFC107"; // Yellow - low
  if (intensity < 600) return "#FF9800"; // Orange - medium
  if (intensity < 800) return "#FF5722"; // Deep orange - high
  return "#D32F2F"; // Red - very high
};
